import { useCallback, useRef, useState } from "react"

import {
  isLookupZip,
  type ZipFill,
  type ZipMatch,
  zipFillFrom
} from "../components/AutoComplete/zip-lookup.ts"
import { autocompleteZip } from "../services/shared/address-autocomplete"
import { useDebouncedCallback } from "./use-debounced-callback.ts"

export type ZipLookupStatus =
  | { kind: "idle" }
  | { kind: "unknown" }
  | { kind: "failed" }
  | { kind: "unsupportedState"; state: string }
  | { kind: "choices"; matches: ZipMatch[] }
  | { kind: "cityMismatch"; zip: string; city: string; matches: ZipMatch[] }

interface UseZipLookupOptions {
  onFill: (fill: ZipFill) => void
  knownStates?: string[]
  disabled?: boolean
  /**
   * The ZIP the form opened with, if any. Its city and state came from
   * somewhere, so typing it again must not replace them. A form that starts
   * empty passes nothing.
   */
  initialZip?: string | null
}

/** The address autocomplete's own pacing, so the two controls behave alike. */
const DEBOUNCE_MS = 700

const sameCity = (a: string, b: string) =>
  a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase()

const mismatchKey = (zip: string, city: string) =>
  `${zip}|${city.trim().toLocaleLowerCase()}`

export function useZipLookup({
  onFill,
  knownStates = [],
  disabled,
  initialZip
}: UseZipLookupOptions) {
  const [status, setStatus] = useState<ZipLookupStatus>({ kind: "idle" })
  const appliedZip = useRef<string | null>((initialZip ?? "").trim() || null)
  const pendingZip = useRef<string | null>(null)
  const lookupRequestId = useRef(0)
  const checkRequestId = useRef(0)
  const armed = useRef(0)
  const checkArmed = useRef(0)
  const declined = useRef<string | null>(null)

  /**
   * Abandon both halves of a lookup in progress: the keystroke still waiting out
   * the debounce, and the request already on the wire. Either would otherwise
   * land on fields that have since been filled from somewhere else.
   */
  const discardPending = useCallback(() => {
    armed.current++
    checkArmed.current++
    lookupRequestId.current++
    checkRequestId.current++
  }, [])

  const apply = useCallback(
    (match: ZipMatch, zip: string) => {
      const fill = zipFillFrom(match, knownStates)
      appliedZip.current = zip
      // A check in flight judges the city this fill is about to replace.
      if (fill.city !== undefined) checkRequestId.current++
      onFill(fill)
      setStatus(
        fill.state === undefined && match.state
          ? { kind: "unsupportedState", state: match.state }
          : { kind: "idle" }
      )
    },
    [knownStates, onFill]
  )

  const run = useCallback(
    async (value: string, token: number) => {
      if (token !== armed.current) return

      const zip = (value ?? "").trim()

      if (disabled || !isLookupZip(zip)) {
        setStatus({ kind: "idle" })
        return
      }

      // A city the user corrected by hand must survive typing the same ZIP again.
      if (appliedZip.current === zip) return

      const id = ++lookupRequestId.current
      pendingZip.current = zip

      let response
      try {
        response = await autocompleteZip({ q: zip })
      } catch {
        response = undefined
      }

      // A slower earlier request must not overwrite a later one's answer.
      if (id !== lookupRequestId.current) return

      // A server that is down, offline, or answering a shape we do not know is
      // not the same thing as a ZIP that does not exist, and saying so would tell
      // the user their correct ZIP is invalid.
      const matches = response?.ok ? response.data?.suggestions : undefined
      if (!Array.isArray(matches)) {
        setStatus({ kind: "failed" })
        return
      }

      if (!matches.length) {
        setStatus({ kind: "unknown" })
        return
      }

      if (matches.length === 1) {
        apply(matches[0], zip)
        return
      }

      setStatus({ kind: "choices", matches })
    },
    [apply, disabled]
  )

  const lookupDebounced = useDebouncedCallback(run, DEBOUNCE_MS)

  const lookupZip = useCallback(
    (value: string) => {
      checkArmed.current++
      checkRequestId.current++
      lookupDebounced(value, ++armed.current)
    },
    [lookupDebounced]
  )

  const verifyCity = useCallback(
    async (zipValue: string, cityValue: string, token: number) => {
      if (token !== checkArmed.current) return

      const zip = (zipValue ?? "").trim()
      const city = (cityValue ?? "").trim()
      const dropMismatch = () =>
        setStatus((current) =>
          current.kind === "cityMismatch" ? { kind: "idle" } : current
        )

      if (disabled || !isLookupZip(zip) || !city) {
        dropMismatch()
        return
      }
      if (declined.current === mismatchKey(zip, city)) return

      const id = ++checkRequestId.current

      let response
      try {
        response = await autocompleteZip({ q: zip })
      } catch {
        response = undefined
      }

      if (id !== checkRequestId.current) return

      // A check is a courtesy: an answer it cannot read says nothing at all,
      // rather than blaming a ZIP nobody asked it to judge.
      const matches = response?.ok ? response.data?.suggestions : undefined
      const named = Array.isArray(matches)
        ? matches.filter((match) => match.city)
        : []
      if (!named.length || named.some((match) => sameCity(match.city, city))) {
        dropMismatch()
        return
      }

      pendingZip.current = zip
      setStatus({ kind: "cityMismatch", zip, city, matches: named })
    },
    [disabled]
  )

  const verifyDebounced = useDebouncedCallback(verifyCity, DEBOUNCE_MS)

  /**
   * Compare a city the user entered with the one its ZIP is delivered to, and
   * offer the ZIP's city when they differ. Never writes a field: the offer is
   * taken through `chooseMatch`, or declined through `declineCityMismatch`.
   */
  const checkCity = useCallback(
    (zip: string | null | undefined, city: string | null | undefined) =>
      verifyDebounced(zip ?? "", city ?? "", ++checkArmed.current),
    [verifyDebounced]
  )

  const declineCityMismatch = useCallback(() => {
    if (status.kind !== "cityMismatch") return
    declined.current = mismatchKey(status.zip, status.city)
    setStatus({ kind: "idle" })
  }, [status])

  const chooseMatch = useCallback(
    (match: ZipMatch) => apply(match, pendingZip.current ?? match.zip_code),
    [apply]
  )

  /**
   * Record a ZIP filled by something else — choosing a full address sets city,
   * state and ZIP together, and looking that ZIP up would replace a borough with
   * the postal city.
   */
  const suppressLookupFor = useCallback(
    (zip: string | null | undefined) => {
      appliedZip.current = (zip ?? "").trim() || null
      discardPending()
      setStatus({ kind: "idle" })
    },
    [discardPending]
  )

  /** Takes the hint down without forgetting which ZIP the fields reflect. */
  const clearStatus = useCallback(() => {
    discardPending()
    setStatus({ kind: "idle" })
  }, [discardPending])

  return {
    lookupZip,
    checkCity,
    chooseMatch,
    declineCityMismatch,
    suppressLookupFor,
    clearStatus,
    status
  }
}
